import { $api } from "@/kibi/core/utils/api";
import type {
  PaginatedResponse,
  QueryOptions,
} from "@/kibi/core/utils/pagination_types";
import type { InjectionKey } from "vue";
import type {
  CreateOrganizationRequest,
  OrganizationDTO,
  OrganizationMetadataDTO,
  UpdateOrganizationRequest,
} from "../models/organization_model";

export interface OrganizationDataSource {
  getOrganizations(id: string): Promise<OrganizationDTO[]>;
  getOrganization(id: string): Promise<OrganizationDTO>;
  getPendingOrganizations(): Promise<OrganizationDTO[]>;
  getOrganizationMetadata(): Promise<OrganizationMetadataDTO>;
  createOrganization(
    request: CreateOrganizationRequest,
  ): Promise<OrganizationDTO>;
  updateOrganization(
    id: string,
    request: UpdateOrganizationRequest,
  ): Promise<OrganizationDTO>;
  deleteOrganization(id: string): Promise<void>;
  updateOrganizationLogo(id: string, file: File): Promise<string>;
  removeOrganizationLogo(id: string): Promise<OrganizationDTO>;
  resendInvite(id: string, email: string): Promise<void>;
  approveOrganization(id: string): Promise<void>;
  getOrganizationUsers(id: string): Promise<any[]>;
  getSubordinates(id: string): Promise<OrganizationDTO[]>;
  getAllOrganizations(
    options: QueryOptions,
  ): Promise<PaginatedResponse<OrganizationDTO[]>>;
  listOrganizationsForSplit(): Promise<OrganizationDTO[]>;
}

export const organizationDataSourceKey: InjectionKey<OrganizationDataSource> =
  Symbol("OrganizationDataSource");

export class OrganizationDataSourceImpl implements OrganizationDataSource {
  async getOrganizations(id: string): Promise<OrganizationDTO[]> {
    const response = await $api<{ data: OrganizationDTO[] }>(
      `/accounts/organizations/${id}/child-orgs`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async getOrganization(id: string): Promise<OrganizationDTO> {
    const response = await $api<{ data: OrganizationDTO }>(
      `/accounts/organizations/${id}`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async getPendingOrganizations(): Promise<OrganizationDTO[]> {
    const response = await $api<{ data: OrganizationDTO[] }>(
      "/accounts/organizations/pending",
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async getOrganizationMetadata(): Promise<OrganizationMetadataDTO> {
    const response = await $api<{ data: OrganizationMetadataDTO }>(
      "/accounts/organizations/metadata",
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async createOrganization(
    request: CreateOrganizationRequest,
  ): Promise<OrganizationDTO> {
    const response = await $api<{ data: OrganizationDTO }>(
      "/accounts/organizations",
      {
        method: "POST",
        body: request,
      },
    );
    return response.data;
  }

  async updateOrganization(
    id: string,
    request: UpdateOrganizationRequest,
  ): Promise<OrganizationDTO> {
    const response = await $api<{ data: OrganizationDTO }>(
      `/accounts/organizations/${id}`,
      {
        method: "PUT",
        body: request,
      },
    );
    return response.data;
  }

  async deleteOrganization(id: string): Promise<void> {
    await $api(`/accounts/organizations/${id}`, {
      method: "DELETE",
    });
  }

  async updateOrganizationLogo(id: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append("logo", file);

    const response = await $api<{ data: string }>(
      `/accounts/organizations/${id}/logo`,
      {
        method: "PUT",
        body: formData,
      },
    );
    return response.data;
  }

  async removeOrganizationLogo(id: string): Promise<OrganizationDTO> {
    const response = await $api<{ data: OrganizationDTO }>(
      `/accounts/organizations/${id}/logo`,
      {
        method: "DELETE",
      },
    );
    return response.data;
  }

  async resendInvite(id: string, email: string): Promise<void> {
    await $api(`/accounts/organizations/${id}/resend-invite`, {
      method: "POST",
      body: { email },
    });
  }

  async approveOrganization(id: string): Promise<void> {
    await $api(`/accounts/organizations/${id}/approve`, {
      method: "POST",
    });
  }

  async getSubordinates(id: string): Promise<OrganizationDTO[]> {
    const response = await $api<{ data: OrganizationDTO[] }>(
      `/accounts/organizations/${id}/child-orgs`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async getOrganizationUsers(id: string): Promise<any[]> {
    const response = await $api<{ data: any[] }>(
      `/accounts/organizations/${id}/users`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async getAllOrganizations(
    options: QueryOptions,
  ): Promise<PaginatedResponse<OrganizationDTO[]>> {
    const response = await $api<PaginatedResponse<OrganizationDTO[]>>(
      "/accounts/organizations/all",
      {
        method: "GET",
        params: options,
      },
    );
    return response;
  }

  async listOrganizationsForSplit(): Promise<OrganizationDTO[]> {
    const response = await $api<{ data: OrganizationDTO[] }>(
      "/accounts/organizations/split",
      {
        method: "GET",
      },
    );
    return response.data;
  }
}
